import { useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select, Switch } from '@ds/forms';
import { PageHeader } from '@/comuns/PageHeader';
import { moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import type { CargaDoFaturamento, DadosDoPlano, Plano } from '@/data/tipos';

export async function carregarFaturamento(): Promise<CargaDoFaturamento> {
    return dados().faturamento();
}

/** "199,90" vira 19990. Vazio vira ausência de preço, que não é zero. */
function precoEmCentavos(texto: string): number | null {
    const limpo = texto.trim();
    if (!limpo) return null;
    const n = Number(limpo.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? Math.round(n * 100) : null;
}

const VAZIO: DadosDoPlano = { codigo: '', nome: '', ciclo: 'mensal', ativo: true };

export function Planos() {
    const carga = useLoaderData() as CargaDoFaturamento;
    const revalidator = useRevalidator();
    const aviso = useAviso();

    const [emEdicao, setEmEdicao] = useState<Plano | 'novo' | null>(null);
    const [form, setForm] = useState<DadosDoPlano>(VAZIO);
    const [preco, setPreco] = useState('');
    const formSujo = useAlteracaoPendente({ form, preco }, emEdicao);
    const [salvando, setSalvando] = useState(false);

    const abrir = (plano: Plano | 'novo') => {
        setEmEdicao(plano);
        if (plano === 'novo') {
            setForm(VAZIO);
            setPreco('');
            return;
        }
        setForm({
            codigo: plano.codigo, nome: plano.nome, ciclo: plano.ciclo, ativo: plano.ativo,
            ...(plano.limiteUnidades === undefined ? {} : { limiteUnidades: plano.limiteUnidades }),
            ...(plano.limiteMembros === undefined ? {} : { limiteMembros: plano.limiteMembros }),
        });
        setPreco(plano.precoCentavos === undefined ? '' : (plano.precoCentavos / 100).toFixed(2).replace('.', ','));
    };

    const salvar = async () => {
        if (!emEdicao) return;
        setSalvando(true);
        try {
            await dados().salvarPlano(emEdicao === 'novo' ? null : emEdicao.id, {
                ...form,
                precoCentavos: precoEmCentavos(preco),
            });
            aviso({ title: emEdicao === 'novo' ? 'Plano criado' : 'Plano salvo', description: form.nome || form.codigo });
            setEmEdicao(null);
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível salvar o plano', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const colunas: DataTableColumn<Plano>[] = [
        {
            key: 'nome', header: 'Plano',
            render: (p) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{p.nome}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {p.codigo} · {p.ciclo}
                        {p.limiteUnidades ? ` · até ${p.limiteUnidades} unidades` : ''}
                        {p.limiteMembros ? ` · até ${p.limiteMembros} pessoas` : ''}
                    </span>
                </span>
            ),
        },
        {
            key: 'precoCentavos', header: 'Preço', align: 'right', width: 170,
            /* Ausência é dita com palavra, não com "R$ 0,00" — que seria um
               plano de graça, e não um preço por decidir. */
            render: (p) => (p.precoCentavos === undefined
                ? <span style={{ color: 'var(--warning)' }}>a definir</span>
                : <strong style={{ fontWeight: 600 }}>{moeda(p.precoCentavos)}</strong>),
        },
        { key: 'clinicas', header: 'Clínicas', align: 'right', width: 110 },
        {
            key: 'ativo', header: 'Situação', width: 120,
            render: (p) => <Badge tone={p.ativo ? 'success' : 'neutral'}>{p.ativo ? 'Ativo' : 'Inativo'}</Badge>,
        },
        {
            key: 'acoes', header: '', width: 100, align: 'right',
            render: (p) => <Button size="sm" variant="secondary" onClick={() => abrir(p)}>Editar</Button>,
        },
    ];

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Planos e assinaturas"
                description="O catálogo que a cobrança usa. Cada clínica tem uma assinatura, e ela aponta para um destes."
                actions={<Button iconLeft={<Icon name="plus" size={15} />} onClick={() => abrir('novo')}>Novo plano</Button>}
            />

            {!carga.temPreco && (
                <Alert tone="warning" dismissible={false} title="Nenhum plano tem preço definido">
                    Enquanto o preço não existir, não há fatura para gerar nem receita para somar — e as telas
                    que dependem de valor dizem isso em vez de mostrar zero. Definir o preço aqui liga o resto.
                </Alert>
            )}

            <Card padding={0}>
                <DataTable
                    columns={colunas} rows={carga.planos} rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhum plano cadastrado."
                />
            </Card>

            <Card title="Assinaturas por clínica" subtitle="A situação é a da clínica — não existe uma segunda para discordar dela" padding={0}>
                <DataTable
                    columns={[
                        { key: 'clinica', header: 'Clínica', render: (a) => <strong style={{ fontWeight: 600 }}>{a.clinica}</strong> },
                        { key: 'plano', header: 'Plano', width: 160 },
                        { key: 'situacao', header: 'Situação', width: 140 },
                        {
                            key: 'precoCentavos', header: 'Preço', align: 'right', width: 140,
                            render: (a) => (a.precoCentavos === undefined ? '—' : moeda(a.precoCentavos)),
                        },
                        {
                            key: 'proximaCobranca', header: 'Próxima cobrança', align: 'right', width: 170,
                            render: (a) => a.proximaCobranca ?? (a.canceladaEm ? 'cancelada' : '—'),
                        },
                    ]}
                    rows={carga.assinaturas} rowKey="clinicaId"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhuma assinatura."
                />
            </Card>

            <Modal
                open={emEdicao !== null}
                title={emEdicao === 'novo' ? 'Novo plano' : 'Editar plano'}
                onClose={() => setEmEdicao(null)}
                dirty={formSujo}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setEmEdicao(null)}>Cancelar</Button>
                        <Button onClick={salvar} disabled={!form.codigo.trim() || salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </>
                )}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 14 }}>
                    <FormField label="Código" htmlFor="plano-codigo" hint="É por ele que a clínica aponta." required>
                        <Input id="plano-codigo" value={form.codigo} onChange={(v) => setForm({ ...form, codigo: v })} placeholder="essencial" />
                    </FormField>
                    <FormField label="Nome" htmlFor="plano-nome">
                        <Input id="plano-nome" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} placeholder="Essencial" />
                    </FormField>
                    <FormField label="Ciclo" htmlFor="plano-ciclo">
                        <Select
                            id="plano-ciclo" value={form.ciclo}
                            onChange={(v) => setForm({ ...form, ciclo: v === 'anual' ? 'anual' : 'mensal' })}
                            options={[{ value: 'mensal', label: 'Mensal' }, { value: 'anual', label: 'Anual' }]}
                        />
                    </FormField>
                    <FormField label="Preço" htmlFor="plano-preco" hint="Deixe vazio para manter “a definir”.">
                        <Input id="plano-preco" value={preco} onChange={setPreco} placeholder="199,90" iconLeft={<span style={{ fontSize: 12 }}>R$</span>} />
                    </FormField>
                    <FormField label="Limite de unidades" htmlFor="plano-unidades">
                        <Input
                            id="plano-unidades" type="number" value={String(form.limiteUnidades ?? '')}
                            onChange={(v) => setForm({ ...form, limiteUnidades: v ? Number(v) : undefined })}
                        />
                    </FormField>
                    <FormField label="Limite de pessoas" htmlFor="plano-membros">
                        <Input
                            id="plano-membros" type="number" value={String(form.limiteMembros ?? '')}
                            onChange={(v) => setForm({ ...form, limiteMembros: v ? Number(v) : undefined })}
                        />
                    </FormField>
                    <Switch checked={form.ativo} onChange={(v) => setForm({ ...form, ativo: v })} label="Ativo" style={{ gridColumn: '1 / -1' }} />
                </div>
            </Modal>
        </div>
    );
}
